import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface DragPosition {
  x: number;
  y: number;
}

/** Position as a fraction (0-1) of the available travel space, not absolute pixels — this is
 * what actually gets persisted, so the widget stays in the same RELATIVE spot (e.g. "75% across,
 * bottom edge") across browser zoom, OS display-scale changes, and window resizes, instead of
 * drifting to a different spot as the CSS-pixel viewport size changes underneath a fixed
 * absolute coordinate. */
interface Fraction {
  x: number;
  y: number;
}

interface UseDraggablePositionOptions {
  /** localStorage key the position is persisted under */
  storageKey: string;
  /** side length (px) of the square draggable element, used for boundary math */
  size: number;
  /** distance (px) kept from viewport edges */
  margin?: number;
  /** movement (px) required before a pointerdown is treated as a drag rather than a click */
  dragThreshold?: number;
  /** distance (px) from a left/right edge that triggers a gentle magnetic snap on release */
  edgeSnapThreshold?: number;
  /** touch-only: hold-in-place duration (ms) that resets the position instead of opening on tap */
  longPressMs?: number;
  /** called right after any reset (long-press on touch, or the caller's own resetPosition() call) */
  onPositionReset?: () => void;
}

interface UseDraggablePositionResult {
  position: DragPosition;
  isDragging: boolean;
  /** true while a touch hold is in progress, for a "charging up" visual cue before it fires */
  isLongPressing: boolean;
  elementRef: React.RefObject<HTMLDivElement>;
  onPointerDown: (e: React.PointerEvent) => void;
  /** call from onClick; returns true (and resets) if the preceding interaction was a drag, not a tap */
  suppressNextClick: () => boolean;
  /** clears the saved position and snaps back to the default bottom-right spot */
  resetPosition: () => void;
}

const DEFAULT_MARGIN = 24;
const DEFAULT_THRESHOLD = 6;
const DEFAULT_SNAP = 16;
const DEFAULT_LONG_PRESS_MS = 550;
const SETTLE_MS = 220;
const DEFAULT_FRACTION: Fraction = { x: 1, y: 1 }; // bottom-right corner

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function clamp01(value: number) {
  return clamp(value, 0, 1);
}

function getViewport() {
  const vv = window.visualViewport;
  const de = document.documentElement;
  // Take the smallest candidate from each source so a stale/mismatched reading (seen in
  // practice across zoom levels, OS display-scale changes, and multi-monitor DPI switches)
  // never leaves the widget believing there's more visible space than there actually is.
  const widthCandidates = [vv?.width, window.innerWidth, de?.clientWidth].filter(
    (n): n is number => typeof n === 'number' && n > 0
  );
  const heightCandidates = [vv?.height, window.innerHeight, de?.clientHeight].filter(
    (n): n is number => typeof n === 'number' && n > 0
  );
  return {
    width: widthCandidates.length ? Math.min(...widthCandidates) : 320,
    height: heightCandidates.length ? Math.min(...heightCandidates) : 480,
  };
}

/** How far (px) the widget can travel between its margin-inset edges, at the current viewport size. */
function getTravel(size: number, margin: number) {
  const { width, height } = getViewport();
  return {
    width: Math.max(0, width - size - margin * 2),
    height: Math.max(0, height - size - margin * 2),
  };
}

function fractionToPixels(frac: Fraction, size: number, margin: number): DragPosition {
  const travel = getTravel(size, margin);
  return {
    x: margin + clamp01(frac.x) * travel.width,
    y: margin + clamp01(frac.y) * travel.height,
  };
}

function pixelsToFraction(pos: DragPosition, size: number, margin: number): Fraction {
  const travel = getTravel(size, margin);
  return {
    x: travel.width > 0 ? clamp01((pos.x - margin) / travel.width) : 0,
    y: travel.height > 0 ? clamp01((pos.y - margin) / travel.height) : 0,
  };
}

function readStoredFraction(storageKey: string): Fraction {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
        return { x: clamp01(parsed.x), y: clamp01(parsed.y) };
      }
    }
  } catch {
    /* corrupt storage — fall back to default */
  }
  return DEFAULT_FRACTION;
}

/**
 * Drag-anywhere-in-viewport positioning for a small fixed-position widget (e.g. a chat
 * launcher). The live drag itself is tracked with plain refs/CSS transforms (no React
 * re-render per pixel); on release the final pixel position is converted to — and persisted
 * as — a fraction of the available travel space, so it stays in the same relative spot
 * (not a fixed absolute pixel offset) across zoom/resize/DPI changes.
 */
export function useDraggablePosition({
  storageKey,
  size,
  margin = DEFAULT_MARGIN,
  dragThreshold = DEFAULT_THRESHOLD,
  edgeSnapThreshold = DEFAULT_SNAP,
  longPressMs = DEFAULT_LONG_PRESS_MS,
  onPositionReset,
}: UseDraggablePositionOptions): UseDraggablePositionResult {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLongPressing, setIsLongPressing] = useState(false);
  const [fraction, setFraction] = useState<Fraction>(() => readStoredFraction(storageKey));
  // Bumped on every viewport-size-affecting event purely to force position to be
  // re-derived from the (unchanged) fraction against the (changed) live viewport.
  const [viewportTick, setViewportTick] = useState(0);

  const position = useMemo(
    () => fractionToPixels(fraction, size, margin),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fraction, size, margin, viewportTick]
  );

  const dragState = useRef<{
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    startPos: DragPosition;
    dragging: boolean;
    pointerId: number | null;
    frame: number | null;
  }>({
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    startPos: { x: 0, y: 0 },
    dragging: false,
    pointerId: null,
    frame: null,
  });

  const wasDraggingRef = useRef(false);
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onPositionResetRef = useRef(onPositionReset);
  onPositionResetRef.current = onPositionReset;

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setIsLongPressing(false);
  }, []);

  const persist = useCallback(
    (frac: Fraction) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(frac));
      } catch {
        /* quota exceeded or unavailable — non-fatal */
      }
    },
    [storageKey]
  );

  const resetPosition = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* non-fatal */
    }
    setFraction(DEFAULT_FRACTION);
    onPositionResetRef.current?.();
  }, [storageKey]);

  // Force position to be re-derived against the live viewport on anything that can change it:
  // window resize, orientation change, mobile keyboard, browser zoom (Ctrl +/-), or an OS
  // display-scale change (e.g. moving the window between a laptop panel and an external
  // monitor). `window.resize` alone doesn't fire reliably for all of these in every browser, so
  // this also uses ResizeObserver (the standard, more reliable zoom signal) and re-checks on
  // visibility/focus changes as a last-resort safety net.
  useEffect(() => {
    const bump = () => setViewportTick((t) => t + 1);

    window.addEventListener('resize', bump);
    window.visualViewport?.addEventListener('resize', bump);
    document.addEventListener('visibilitychange', bump);
    window.addEventListener('focus', bump);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(bump);
      ro.observe(document.documentElement);
    }

    return () => {
      window.removeEventListener('resize', bump);
      window.visualViewport?.removeEventListener('resize', bump);
      document.removeEventListener('visibilitychange', bump);
      window.removeEventListener('focus', bump);
      ro?.disconnect();
    };
  }, []);

  const applyTransform = (dx: number, dy: number) => {
    const el = elementRef.current;
    if (el) el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
  };

  // While actively dragging, the pointer sweeps across ordinary page text/cards on its way to
  // the drop point; without this the browser's native text-selection drag fires in parallel
  // (independent of the launcher's own `select-none`, since that only covers the launcher
  // itself, not whatever the cursor passes over) and leaves visible highlighted text behind.
  const setPageSelectable = (selectable: boolean) => {
    document.body.style.userSelect = selectable ? '' : 'none';
  };

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      const state = dragState.current;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      state.lastX = e.clientX;
      state.lastY = e.clientY;

      if (!state.dragging) {
        if (Math.abs(dx) < dragThreshold && Math.abs(dy) < dragThreshold) return;
        clearLongPressTimer(); // real movement — this is a drag, not a stationary hold
        state.dragging = true;
        wasDraggingRef.current = true;
        setIsDragging(true);
        setPageSelectable(false);
        const el = elementRef.current;
        if (el) el.style.transition = 'none';
      }

      if (state.frame != null) return;
      state.frame = requestAnimationFrame(() => {
        state.frame = null;
        applyTransform(dx, dy);
      });
    },
    [dragThreshold, clearLongPressTimer]
  );

  const endDrag = useCallback(
    (e: PointerEvent) => {
      const state = dragState.current;
      const el = elementRef.current;

      clearLongPressTimer(); // release before the hold completed — not a long press
      setPageSelectable(true); // no-op if a drag never actually disabled it
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      if (state.frame != null) {
        cancelAnimationFrame(state.frame);
        state.frame = null;
      }

      if (state.dragging) {
        const dx = e.clientX - state.startX;
        const dy = e.clientY - state.startY;
        const travel = getTravel(size, margin);
        let next = {
          x: clamp(state.startPos.x + dx, margin, margin + travel.width),
          y: clamp(state.startPos.y + dy, margin, margin + travel.height),
        };

        // Gentle magnetic snap to whichever horizontal edge is close
        const distToLeft = next.x - margin;
        const distToRight = margin + travel.width - next.x;
        if (Math.min(distToLeft, distToRight) <= edgeSnapThreshold) {
          next = { ...next, x: distToLeft <= distToRight ? margin : margin + travel.width };
        }

        if (el) {
          el.style.transition = `transform ${SETTLE_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1)`;
          el.style.transform = `translate3d(${next.x - state.startPos.x}px, ${next.y - state.startPos.y}px, 0)`;
        }

        const nextFraction = pixelsToFraction(next, size, margin);
        if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
        settleTimeoutRef.current = setTimeout(() => {
          const node = elementRef.current;
          if (node) {
            node.style.transition = 'none';
            node.style.transform = 'none';
          }
          setFraction(nextFraction);
          persist(nextFraction);
        }, SETTLE_MS);
      }

      setIsDragging(false);
      state.dragging = false;
      state.pointerId = null;
    },
    [onPointerMove, size, margin, edgeSnapThreshold, persist, clearLongPressTimer]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return; // left-click only for mouse
      dragState.current = {
        startX: e.clientX,
        startY: e.clientY,
        lastX: e.clientX,
        lastY: e.clientY,
        startPos: position,
        dragging: false,
        pointerId: e.pointerId,
        frame: null,
      };
      // Reset here, not just on consumption: a drag with no follow-up click (rare, but
      // possible) must not leave a stale flag that wrongly eats some later, unrelated click.
      wasDraggingRef.current = false;
      // No setPointerCapture: move/up are tracked via window-level listeners regardless of
      // where the pointer physically is, and capturing on a non-target ancestor was found to
      // suppress the browser's native click synthesis after a genuine (non-drag) tap.
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);

      // Touch has no right-click, so a held-in-place tap is the reset gesture instead.
      // Cancelled by any real movement (onPointerMove) or an early release (endDrag).
      if (e.pointerType === 'touch') {
        setIsLongPressing(true);
        longPressTimerRef.current = setTimeout(() => {
          longPressTimerRef.current = null;
          setIsLongPressing(false);
          wasDraggingRef.current = true; // suppress the tap-to-open that follows on release
          resetPosition();
        }, longPressMs);
      }
    },
    [position, onPointerMove, endDrag, longPressMs, resetPosition]
  );

  const suppressNextClick = useCallback(() => {
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      return true;
    }
    return false;
  }, []);

  // Clean up any in-flight drag listeners/timers if the component unmounts mid-drag.
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (dragState.current.frame != null) cancelAnimationFrame(dragState.current.frame);
      setPageSelectable(true);
    };
  }, [onPointerMove, endDrag]);

  // Without pointer capture, a release that happens outside the browser window (or a tab/app
  // switch mid-drag) never fires pointerup here. Force-settle using the last known point so the
  // widget doesn't stay visually "stuck" mid-drag.
  useEffect(() => {
    const onBlur = () => {
      const state = dragState.current;
      if (state.dragging) {
        endDrag({ clientX: state.lastX, clientY: state.lastY } as PointerEvent);
      }
    };
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, [endDrag]);

  return { position, isDragging, isLongPressing, elementRef, onPointerDown, suppressNextClick, resetPosition };
}
